import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { assetLabel, formatCompact, formatPct, formatPrice } from '../core/format';
import { AssetClass } from '../core/models';
import { MarketService } from '../core/market.service';
import { LanguageService } from '../core/language';
import { Sparkline } from '../ui/sparkline';

const FILTERS: Array<{ id: 'ALL' | AssetClass; label: string }> = [
  { id: 'ALL', label: 'All' },
  { id: 'EQUITY', label: 'Equities' },
  { id: 'INDEX', label: 'Indexes' },
  { id: 'CRYPTO', label: 'Crypto' },
  { id: 'FX', label: 'FX' },
  { id: 'COMMODITY', label: 'Metals' },
];

@Component({
  selector: 'app-markets',
  imports: [RouterLink, Sparkline],
  templateUrl: './markets.html',
})
export class MarketsPage {
  protected readonly market = inject(MarketService);
  protected readonly lang = inject(LanguageService);
  protected readonly filters = FILTERS;
  protected readonly filter = signal<(typeof FILTERS)[number]['id']>('ALL');
  protected readonly query = signal('');
  protected readonly price = formatPrice;
  protected readonly pct = formatPct;
  protected readonly compact = formatCompact;
  protected readonly label = assetLabel;

  constructor() {
    inject(ActivatedRoute).queryParamMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      this.query.set(params.get('q') ?? '');
      const klass = params.get('class');
      const known = FILTERS.some((item) => item.id === klass);
      this.filter.set(known ? (klass as (typeof FILTERS)[number]['id']) : 'ALL');
    });
  }

  protected readonly rows = computed(() => {
    const needle = this.query().trim().toLowerCase();
    const filter = this.filter();
    return this.market.quotes().filter((quote) => {
      const matchesFilter = filter === 'ALL' || quote.assetClass === filter;
      const matchesQuery =
        needle.length === 0 ||
        quote.symbol.toLowerCase().includes(needle) ||
        quote.name.toLowerCase().includes(needle);
      return matchesFilter && matchesQuery;
    });
  });
}
